import { 
  Avatar, 
  Badge,
  Divider, 
  Drawer,
  Icon, 
  List, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Menu, 
  MenuItem, 
  Typography, 
  useMediaQuery, 
  useTheme,
  Box,
  styled,
  alpha
} from '@mui/material';
import { useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { useAppThemeContext, useAuthContext, useDrawerContext } from '../../contexts';
import { Environment } from '../../environment';
import { DialogPefilFoto } from './DialogPefilFoto';
import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { DialogPefil } from './DialogPerfil';

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 2),
  padding: theme.spacing(1, 2),
  transition: 'all 0.3s ease',
}));
const HideScrollbarBox = styled(Box)({
  overflowY: 'auto',
  scrollbarWidth: 'none', 
  '&::-webkit-scrollbar': {
    display: 'none',  
  },
});
interface IListItemLinkProps {
  to: string;
  icon: string;
  label: string;
  notificacao: string;
  onClick: (() => void) | undefined;
}

const ListItemLink: React.FC<IListItemLinkProps> = ({
  to,
  icon,
  label,
  notificacao,
  onClick,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const resolvedPath = useResolvedPath(to);
  const match = useMatch({ path: resolvedPath.pathname, end: false });

  const handleClick = () => {
    navigate(to);
    onClick?.();
  };

  return (
    <StyledListItemButton selected={!!match} onClick={handleClick}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Icon fontSize="small" color={match ? 'primary' : 'inherit'}>{icon}</Icon>
      </ListItemIcon>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={1}>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: match ? 'medium' : 'regular',
            color: match ? theme.palette.primary.main : theme.palette.text.primary,
          }}
        />
        {notificacao && parseInt(notificacao) > 0 && (
          <Badge
            badgeContent={notificacao}
            color="primary"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                right: -4,
                top: 4,
                minWidth: 20,
                height: 20,
                borderRadius: '50%',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                backgroundColor: theme.palette.mode === 'light' 
                  ? theme.palette.primary.light 
                  : theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              }
            }}
          />
        )}
      </Box>
    </StyledListItemButton>
  );
};

interface IMenuLateralProps {
  children: React.ReactNode
}
export const MenuLateral: React.FC<IMenuLateralProps> = ({ children }) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { isDrawerOpen, drawerOptions, toggleDrawerOpen } = useDrawerContext();
  const { toggleTheme } = useAppThemeContext();
  const { logout } = useAuthContext();
  const [openFoto, setOpenFoto] = useState(false);
  const [openPerfil, setOpenPerfil] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fotoperfil, setFotoperfil] = useState('');
  const [iduser, setIduser] = useState('');
  const open = Boolean(anchorEl);
  interface JwtPayload {
    uid: string;
    img: string;
  }

  const handleCloseFoto = () => {
    setOpenFoto(false);
  };
  const handleClosePerfil = () => {
    setOpenPerfil(false);
  };
  const handleFotoPerfil = () => {
    setOpenFoto(true);
    setAnchorEl(null);
  };
  const handlePerfil = () => {
    setOpenPerfil(true);
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose1 = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    const accessToken = localStorage.getItem('APP_ACCESS_TOKEN');
    if (accessToken) {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      setFotoperfil(Environment.URL_IMGUSUARIOS + decoded.img);
      setIduser(decoded.uid);
    }
  }, []);

  return (
    <>
      <Drawer 
        open={isDrawerOpen} 
        variant={smDown ? 'temporary' : 'permanent'} 
        onClose={toggleDrawerOpen}
        PaperProps={{
          sx: {
            width: theme.spacing(30),
            backgroundColor: theme.palette.background.default,
            borderRight: 'none',
            boxShadow: theme.shadows[3],
          }
        }}
      >
        <Box 
          width="100%" 
          height="100%" 
          display="flex" 
          flexDirection="column"
          sx={{
            background: theme.palette.mode === 'light'
              ? 'linear-gradient(180deg, rgba(247, 249, 252, 0.9) 0%, rgba(255, 255, 255, 1) 100%)'
              : 'linear-gradient(180deg, rgba(18, 18, 18, 0.9) 0%, rgba(33, 33, 33, 1) 100%)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box 
            width="100%" 
            height={theme.spacing(18)} 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center"
            sx={{
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, rgba(203, 206, 221, 0.1) 0%, rgba(247, 248, 252, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(232, 232, 237, 0.2) 0%, rgba(237, 238, 242, 0.3) 100%)',
            }}
          >
            <Box
              id="avatar-button"
              aria-controls={open ? 'avatar-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              sx={{ 
                cursor: 'pointer',
                position: 'relative',
                '&:hover::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      border: `2px solid ${theme.palette.background.paper}`,
                    }}
                  />
                }
              >
                <Avatar 
                  sx={{ 
                    height: theme.spacing(10), 
                    width: theme.spacing(10),
                    boxShadow: theme.shadows[3],
                  }}
                  src={fotoperfil}
                />
              </Badge>
            </Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mt: 1,
                fontWeight: 'medium',
                color: theme.palette.text.primary,
              }}
            >
              Meu Perfil
            </Typography>
            <Menu
              id="avatar-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose1}
              MenuListProps={{
                'aria-labelledby': 'avatar-button',
              }}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: 2,
                  minWidth: 180,
                  boxShadow: theme.shadows[10],
                }
              }}
            >
              <MenuItem onClick={handleFotoPerfil} sx={{ py: 1 }}>
                <ListItemIcon>
                  <Icon fontSize="small">photo_camera</Icon>
                </ListItemIcon>
                <ListItemText>Foto do Perfil</ListItemText>
              </MenuItem>
              <MenuItem onClick={handlePerfil} sx={{ py: 1 }}>
                <ListItemIcon>
                  <Icon fontSize="small">person</Icon>
                </ListItemIcon>
                <ListItemText>Meu Perfil</ListItemText>
              </MenuItem>
            </Menu> 
          </Box>
          
          <Divider sx={{ my: 1 }} />
          <HideScrollbarBox flex={1} sx={{ py: 1 }}>
            <List component="nav" dense>
              {drawerOptions.map((drawerOption) => (
                <ListItemLink
                  to={drawerOption.path}
                  key={drawerOption.path}
                  icon={drawerOption.icon}
                  label={drawerOption.label}
                  notificacao={drawerOption.notificacao || "0"}
                  onClick={smDown ? toggleDrawerOpen : undefined}
                />
              ))}
            </List>
          </HideScrollbarBox>
          <Box sx={{ p: 2 }}>
            <List component="nav" dense>
              <StyledListItemButton onClick={toggleTheme}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon>{theme.palette.mode === 'dark' ? 'light_mode' : 'dark_mode'}</Icon>
                </ListItemIcon>
                <ListItemText 
                  primary={theme.palette.mode === 'dark' ? 'Modo Claro' : 'Modo Escuro'} 
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              </StyledListItemButton>
              
              <StyledListItemButton onClick={logout}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon>logout</Icon>
                </ListItemIcon>
                <ListItemText 
                  primary="Sair" 
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              </StyledListItemButton>
            </List>
          </Box>
        </Box>
      </Drawer>
      
      <DialogPefilFoto
        id={iduser}
        img={fotoperfil}
        value={''}  
        open={openFoto} 
        onClose={handleCloseFoto}
      /> 
      
      <DialogPefil
        id={iduser}
        value={''}  
        open={openPerfil} 
        onClose={handleClosePerfil}
      /> 
      
      <Box 
        height="100vh" 
        marginLeft={smDown ? 0 : theme.spacing(30)}
        sx={{
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: theme.palette.background.default,
        }}
      >
        {children}
      </Box>
    </>
  );
};